import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, FileText, Trash2, Download, Loader2, Eye } from "lucide-react";
import api, { API } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch (e) {
      toast.error("Impossibile caricare i documenti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (file, label) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Sono ammessi solo file PDF");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    if (label) form.append("label", label);
    setUploading(true);
    try {
      await api.post("/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const preview = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewName(doc.original_filename);
    } catch (e) {
      toast.error("Errore apertura documento");
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.original_filename}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Documento eliminato");
      setDocs(d => d.filter(x => x.id !== doc.id));
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <div className="pt-28 pb-16 bg-[#F1F5F9] min-h-screen" data-testid="documents-page">
      <div className="container-editorial">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-8 border-b border-[#E5E7EB]">
          <div>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Documenti</p>
            <h1 className="font-serif text-5xl md:text-6xl mt-3 leading-[0.9] text-[#2d6fc4]">
              I tuoi <em className="italic text-[#2d6fc4]">file PDF</em>.
            </h1>
            <p className="mt-2 text-[#333333] text-sm">Carica e consulta i documenti collegati alla tua pratica.</p>
          </div>
          <button
            data-testid="doc-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-cta inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm disabled:opacity-60"
          >
            {uploading ? <><Loader2 size={16} className="animate-spin"/> Caricamento…</> : <><UploadCloud size={16}/> Carica PDF</>}
          </button>
          <input ref={fileRef} type="file" accept="application/pdf" onChange={onPick} className="hidden" data-testid="doc-file-input"/>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="doc-dropzone"
          className={`mt-10 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${dragging ? "border-[#14417B] bg-[#F8FAFC]" : "border-[#E5E7EB] bg-white"}`}
        >
          <UploadCloud size={36} strokeWidth={1.4} className="mx-auto text-[#2d6fc4]"/>
          <p className="mt-4 font-serif text-2xl text-[#2d6fc4]">Trascina qui il tuo PDF</p>
          <p className="mt-1 text-sm text-[#333333]">…oppure clicca "Carica PDF" qui sopra. Max 15MB.</p>
        </div>

        {/* Documents grid */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl text-[#2d6fc4]">Archivio</h2>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">{docs.length} file</p>
          </div>
          {loading && <div className="p-10 text-center text-[#333333] bg-white border border-[#E5E7EB] rounded-lg">Caricamento…</div>}
          {!loading && docs.length === 0 && (
            <div className="p-10 text-center text-[#333333] bg-white border border-[#E5E7EB] rounded-lg" data-testid="doc-empty">
              Nessun documento caricato.
            </div>
          )}
          {!loading && docs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {docs.map((d, i) => (
                <motion.div
                  key={d.id}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i*0.04 }}
                  className="case-card bg-white border border-[#E5E7EB] rounded-lg p-5 flex flex-col"
                  data-testid={`doc-card-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded bg-[#F1F5F9] text-[#2d6fc4]"><FileText size={18} strokeWidth={1.5}/></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#2d6fc4] truncate" title={d.original_filename}>{d.original_filename}</p>
                      <p className="mt-1 text-xs text-[#333333]">{fmtSize(d.size)} · {fmtDate(d.created_at)}</p>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-[#E5E7EB] flex items-center justify-between text-sm">
                    <button onClick={() => preview(d)} data-testid={`doc-preview-${i}`} className="inline-flex items-center gap-1.5 text-[#2d6fc4] hover:text-[#0F3161]">
                      <Eye size={14}/> Anteprima
                    </button>
                    <div className="flex items-center gap-3">
                      <button onClick={() => download(d)} data-testid={`doc-download-${i}`} className="text-[#333333] hover:text-[#2d6fc4]" title="Scarica"><Download size={14}/></button>
                      <button onClick={() => remove(d)} data-testid={`doc-delete-${i}`} className="text-[#333333] hover:text-red-600" title="Elimina"><Trash2 size={14}/></button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 bg-[#1a4a8a]/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
          data-testid="doc-preview-modal"
        >
          <div className="bg-white rounded-lg w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden" onClick={(e)=>e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
              <p className="font-medium text-[#2d6fc4] truncate">{previewName}</p>
              <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="text-[#333333] hover:text-[#2d6fc4] text-sm">Chiudi ✕</button>
            </div>
            <iframe title="pdf" src={previewUrl} className="flex-1 w-full" />
          </div>
        </div>
      )}
    </div>
  );
}
